import React, { useState, useMemo, useEffect } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import type { YhUploadTableProps } from './interface';
import { formatFileList } from '@src/utils/uploadUtils';

// 10MB 大小限制 (字节)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const YhUploadTable: React.FC<YhUploadTableProps> = ({
  maxCount = 4,
  initialValue,
  allowedTypes = [".jpg", ".png", ".jpeg", ".svg", ".gif", ".pdf", ".doc", ".xls", ".xlsx"],
  onChange,
  fileUrl = (url) => url,
  action,
  uploadData,
  listType = 'picture-card',
  uploadButton,
  maxSize = MAX_FILE_SIZE,
  onUpdateRow,
  rowId,
  ...restProps
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [currentFileList, setCurrentFileList] = useState<any[]>([]);

  // 初始化时处理initialValue
  useEffect(() => {
    if (initialValue && initialValue.length > 0) {
      setCurrentFileList(formatFileList(initialValue));
    }
  }, [initialValue, fileUrl]);

  // 文件类型检查
  const isValidFileType = (fileName: string): boolean => {
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    return allowedTypes.includes(fileExt);
  };

  // 上传前检查
  const beforeUpload = (file: RcFile): boolean | Promise<void> => {
    // 检查文件类型
    if (!isValidFileType(file.name)) {
      message.error(`文件类型不支持，请上传${allowedTypes.join(',')}格式的文件`);
      return Promise.reject(new Error('文件类型不支持'));
    }

    // 检查文件大小
    if (file.size > maxSize) {
      message.error(`文件过大，请上传小于${maxSize / 1024 / 1024}MB的文件`);
      return Promise.reject(new Error('文件过大'));
    }

    return true;
  };

  // 处理文件变化
  const handleChange = (info: any) => {
    // 深拷贝当前文件列表
    let newFileList = [...info.fileList];

    // 更新内部文件列表状态
    setCurrentFileList(newFileList);

    // 如果有更新行数据的回调，并且有rowId
    if (onUpdateRow && rowId) {
      // 检查是否有文件上传成功
      const isUploadSuccess = info.file.status === 'done' && info.file.response;

      let file = {
        imgUrl: '',
        imgName: ''
      };
      if (isUploadSuccess) {
        file = {
          imgUrl: info.file.response.retData,
          imgName: info.file.name
        };
      }

      // 准备所有文件的数组
      const fileList = newFileList.map(file => ({
        imgUrl: file.url || file.response?.retData || file.uid,
        imgName: file.name || '文件'
      }));

      onUpdateRow(rowId, file, fileList);
    }

    // 调用外部onChange，但不依赖外部更新fileList
    if (onChange) {
      onChange(info);
    }
  };

  // 处理预览
  const handlePreview = (file: UploadFile) => {
    const isDoc = file?.name?.toLowerCase().endsWith('.pdf') ||
      file?.name?.toLowerCase().endsWith('.doc') ||
      file?.name?.toLowerCase().endsWith('.docx') ||
      file?.name?.toLowerCase().endsWith('.xls') ||
      file?.name?.toLowerCase().endsWith('.xlsx');

    if (isDoc) {
      window.location.href = file?.url ?? "";
    }
    else {
      setPreviewImage(file?.url || '');
      setPreviewOpen(true);
    }
  };

  // 默认上传按钮
  const defaultUploadButton = listType === 'picture-card'
    ? <PlusOutlined />
    : null;

  return (
    <>
      <Upload
        name="file"
        action={action}
        data={uploadData}
        listType={listType}
        fileList={currentFileList}
        maxCount={maxCount}
        beforeUpload={beforeUpload}
        onPreview={handlePreview}
        onChange={handleChange}
        {...restProps}
      >
        {currentFileList.length >= maxCount ? null : (uploadButton || defaultUploadButton)}
      </Upload>

      <Image
        width={200}
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: previewOpen,
          src: previewImage,
          onVisibleChange: setPreviewOpen,
        }}
      />
    </>
  );
};

export default YhUploadTable; 